import * as React from 'react'

import { animate as motionAnimate, useMotionValue } from 'framer-motion'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import MoneyWithCurrency from 'modules/core/lib/money'

interface Options {
  amount: number
  currency: string
  animate?: boolean
}

const Money = ({ amount, currency, animate = true }: Options) => {
  // Ensure runtime type is integer
  // GraphQL transmits BigInts as strings, which isn't reflected in the types
  amount = parseInt(amount.toString())

  const {
    access: { locale },
  } = useCurrentSession()

  const ref = React.useRef<HTMLSpanElement>(null)
  const motionValue = useMotionValue(amount)

  React.useLayoutEffect(() => {
    if (animate) {
      const unsubscribe = motionValue.on(
        'change',
        (value) =>
          !isNaN(value) &&
          ref.current &&
          (ref.current.textContent = MoneyWithCurrency({
            amount: Math.floor(value),
            currency,
            locale,
          }))
      )
      return unsubscribe
    }
  }, [currency, motionValue, animate, locale])

  useUnsafeEffect(() => {
    if (animate) {
      const controls = motionAnimate(motionValue, amount, {
        type: 'spring',
        duration: 0.3,
        bounce: 0,
      })
      return controls.stop
    }
  }, [amount, motionValue, animate])

  const formattedAmount = MoneyWithCurrency({
    amount,
    currency,
    locale,
  })

  return animate ? <span ref={ref}>{formattedAmount}</span> : <>{formattedAmount}</>
}

export default Money
